/**
 * Assignment Manager — Almacén · «Asignar» (design C).
 * Left: the items (only unassigned ones by default), with checkboxes. Right: the formations,
 * grouped by their type, that may ask for the selected items. Saving replaces the list of the
 * selected items with the formations marked.
 */

const AssignmentManager = {
    _view: 'pending',   // 'pending' (no formation yet) | 'all'
    _category: 'all',
    _search: '',
    _formationSearch: '',
    _selected: new Set(),
    _picked: new Set(),
    _saving: false,

    render() {
        if (!AuthManager.canManageWarehouse()) {
            return '<p class="text-center p-4">Acceso denegado</p>';
        }
        // Forget items that no longer exist (deleted or reloaded).
        const ids = new Set(InventoryService.getAllItems().map(i => String(i.id)));
        this._selected = new Set([...this._selected].filter(id => ids.has(id)));
        const categories = InventoryService.categoriesInUse();
        return `
            <div class="wha-view">
                <section class="wh-card wha-items">
                    <div class="wha-toolbar">
                        <div class="wha-seg" id="wha-tabs" role="tablist" aria-label="Qué elementos ver"></div>
                        <select class="wha-select" aria-label="Categoría" ${UIEvents.attr("change", function(event,captured){AssignmentManager.setCategory(this.value);}, [])}>
                            <option value="all">Todas las categorías</option>
                            ${categories.map(c => `<option value="${Utils.sanitizeHTML(c.id)}" ${this._category === c.id ? 'selected' : ''}>${Utils.sanitizeHTML(c.label)}</option>`).join('')}
                        </select>
                        <span class="wha-spacer"></span>
                        <input type="search" id="wha-search" class="wha-select" placeholder="Buscar elemento o n.º de serie" aria-label="Buscar elemento" value="${Utils.sanitizeHTML(this._search)}"
                            ${UIEvents.attr("input", function(event,captured){AssignmentManager.setSearch(this.value);}, [])}>
                    </div>
                    <div class="wha-list" id="wha-list"></div>
                </section>
                <aside class="wh-card wha-panel" id="wha-panel" aria-label="Formaciones que pueden pedirlo"></aside>
            </div>`;
    },

    // Re-renders the whole tab (after saving, or from the warehouse tabs).
    refresh() {
        const box = document.getElementById('warehouse-assignment-container');
        if (box) SafeHTML.set(box, this.render());
        this.paint();
    },

    init() { this.paint(); },

    paint() {
        this.paintList();
        this.paintPanel();
    },

    _categoryLabel(id) { return InventoryService.categoryLabel(id); },

    _items() {
        const q = this._search.trim().toLowerCase();
        return InventoryService.getAllItems()
            .filter(i => this._view === 'all' || !(i.allowedCourses || []).length)
            .filter(i => this._category === 'all' || i.category === this._category)
            .filter(i => !q || `${i.name || ''} ${i.serialNumber || ''}`.toLowerCase().includes(q))
            .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'es'));
    },

    _coursesText(item) {
        const names = new Map(InventoryService.getCourses().map(c => [String(c.id), c.label]));
        const list = (item.allowedCourses || []).map(id => names.get(String(id))).filter(Boolean);
        if (!list.length) return '';
        return list.length > 2 ? `${list.slice(0, 2).join(', ')} +${list.length - 2}` : list.join(', ');
    },

    paintList() {
        const box = document.getElementById('wha-list');
        if (!box) return;
        const pending = InventoryService.getUnassignedItems().length;
        const total = InventoryService.getAllItems().length;
        SafeHTML.set(document.getElementById('wha-tabs'), [['pending', `Sin asignar · ${pending}`], ['all', `Todos · ${total}`]].map(([id, text]) =>
            `<button type="button" role="tab" class="${this._view === id ? 'is-on' : ''}" aria-selected="${this._view === id}" ${UIEvents.attr("click", function(event,captured){AssignmentManager.setView(String(captured[0]));}, [id])}>${text}</button>`).join(''));
        const items = this._items();
        if (!items.length) {
            SafeHTML.set(box, this._view === 'pending' && !this._search && this._category === 'all'
                ? '<div class="wha-empty"><i class="fas fa-check-circle" aria-hidden="true"></i> Todo el inventario está asignado. En «Todos» puedes cambiar a quién está asignado cada elemento.</div>'
                : '<div class="wha-empty">No hay elementos con estos filtros.</div>');
            return;
        }
        const allOn = items.every(i => this._selected.has(String(i.id)));
        const head = `<label class="wha-row is-head"><input type="checkbox" ${allOn ? 'checked' : ''} aria-label="Seleccionar todos los de la lista"
            ${UIEvents.attr("change", function(event,captured){AssignmentManager.toggleAll(this.checked);}, [])}><span>${allOn ? 'Quitar selección' : `Seleccionar los ${items.length}`}</span></label>`;
        SafeHTML.set(box, head + items.map(item => {
            const id = String(item.id), on = this._selected.has(id), courses = this._coursesText(item);
            return `<label class="wha-row ${on ? 'is-on' : ''}">
                <input type="checkbox" ${on ? 'checked' : ''} aria-label="Seleccionar ${Utils.sanitizeHTML(item.name)}" ${UIEvents.attr("change", function(event,captured){AssignmentManager.toggleItem(String(captured[0]), this.checked);}, [id])}>
                <span class="wha-row-main"><b>${Utils.sanitizeHTML(item.name)}</b><small>${Utils.sanitizeHTML(this._categoryLabel(item.category))} · ${Number(item.totalQuantity ?? item.quantity ?? 0)} ud.${item.serialNumber ? ` · ${Utils.sanitizeHTML(item.serialNumber)}` : ''}</small></span>
                <span class="wha-tag ${courses ? 'is-ok' : ''}" title="${Utils.sanitizeHTML(courses || 'Ninguna formación puede pedirlo')}">${Utils.sanitizeHTML(courses || 'Ninguna')}</span>
            </label>`;
        }).join(''));
    },

    // Formations grouped by their type (Ajustes › Tipos), in the types' order.
    _groups() {
        const formations = typeof FormationService !== 'undefined' ? FormationService.getFormations() : [];
        const q = this._formationSearch.trim().toLowerCase();
        const shown = formations.filter(f => !q || String(f.name || '').toLowerCase().includes(q));
        const types = typeof FormationTypeService !== 'undefined' ? FormationTypeService.getTypes() : [];
        const groups = types.map(t => ({ id: t.id, name: t.pluralName || t.name, list: shown.filter(f => f.type === t.id) })).filter(g => g.list.length);
        const known = new Set(types.map(t => t.id));
        const rest = shown.filter(f => !known.has(f.type));
        if (rest.length) groups.push({ id: '', name: groups.length ? 'Otras' : 'Formaciones', list: rest });
        groups.forEach(g => g.list.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'es')));
        return groups;
    },

    paintPanel() {
        const panel = document.getElementById('wha-panel');
        if (!panel) return;
        const n = this._selected.size, m = this._picked.size;
        const items = InventoryService.getAllItems().filter(i => this._selected.has(String(i.id)));
        // With several items whose lists differ, the marked list replaces each one's.
        const lists = new Set(items.map(i => [...(i.allowedCourses || [])].map(String).sort().join('|')));
        const groups = this._groups();
        const title = !n ? 'Marca elementos' : n === 1 ? Utils.sanitizeHTML(items[0].name) : `${n} elementos`;
        const save = !n ? 'Marca al menos un elemento' : !m ? 'Marca al menos una formación'
            : `Asignar ${n === 1 ? '1 elemento' : `${n} elementos`} a ${m === 1 ? '1 formación' : `${m} formaciones`}`;
        SafeHTML.set(panel, `
            <div class="wha-panel-head">
                <span class="wha-label">Pueden pedirlo</span>
                <h2>${title}</h2>
            </div>
            <div class="wha-panel-body">
                <input type="search" id="wha-formation-search" class="wha-select" placeholder="Buscar formación" aria-label="Buscar formación" value="${Utils.sanitizeHTML(this._formationSearch)}"
                    ${UIEvents.attr("input", function(event,captured){AssignmentManager.setFormationSearch(this.value);}, [])}>
                <div class="wha-groups">
                ${groups.length ? groups.map(g => {
                    const allOn = g.list.every(f => this._picked.has(String(f.id)));
                    return `<div class="wha-group">
                        <div class="wha-group-head"><span class="wha-label">${Utils.sanitizeHTML(g.name)}</span>
                            <button type="button" class="wha-link" ${UIEvents.attr("click", function(event,captured){AssignmentManager.toggleGroup(captured[0], !captured[1]);}, [g.list.map(f => String(f.id)), allOn])}>${allOn ? 'Quitar el tipo' : 'Todo el tipo'}</button></div>
                        <div class="wha-formations">${g.list.map(f => {
                            const id = String(f.id), on = this._picked.has(id);
                            return `<label class="wha-formation ${on ? 'is-on' : ''}"><input type="checkbox" ${on ? 'checked' : ''} ${UIEvents.attr("change", function(event,captured){AssignmentManager.toggleFormation(String(captured[0]), this.checked);}, [id])}><span>${Utils.sanitizeHTML(f.name)}</span></label>`;
                        }).join('')}</div>
                    </div>`;
                }).join('') : '<p class="wha-muted">No hay formaciones con ese nombre.</p>'}
                </div>
                ${n > 1 && lists.size > 1 ? '<p class="wha-muted">Los elementos marcados tienen listas distintas: se guardará esta en todos.</p>' : ''}
                <button type="button" class="wha-save" ${n && m && !this._saving ? '' : 'disabled'} ${UIEvents.attr("click", function(event,captured){AssignmentManager.save();}, [])}>${this._saving ? 'Guardando…' : save}</button>
            </div>`);
    },

    // The panel starts from what all the selected items already share.
    _syncPicked() {
        const items = InventoryService.getAllItems().filter(i => this._selected.has(String(i.id)));
        if (!items.length) { this._picked = new Set(); return; }
        const [first, ...rest] = items.map(i => new Set((i.allowedCourses || []).map(String)));
        this._picked = new Set([...first].filter(id => rest.every(s => s.has(id))));
    },

    // From «Importar»: open with the items just created already marked.
    preselect(ids) {
        this._view = 'pending'; this._category = 'all'; this._search = '';
        this._selected = new Set(ids.map(String));
        this._syncPicked();
    },

    setView(view) { this._view = view === 'all' ? 'all' : 'pending'; this.paintList(); },
    setCategory(value) { this._category = value || 'all'; this.paintList(); },
    setSearch(value) { this._search = String(value || ''); this.paintList(); },
    setFormationSearch(value) {
        this._formationSearch = String(value || '');
        this.paintPanel();
        const input = document.getElementById('wha-formation-search');
        if (input) { input.focus(); input.setSelectionRange?.(input.value.length, input.value.length); }
    },

    toggleItem(id, on) {
        if (on) this._selected.add(id); else this._selected.delete(id);
        this._syncPicked();
        this.paint();
    },
    toggleAll(on) {
        this._items().forEach(i => { if (on) this._selected.add(String(i.id)); else this._selected.delete(String(i.id)); });
        this._syncPicked();
        this.paint();
    },
    toggleFormation(id, on) {
        if (on) this._picked.add(id); else this._picked.delete(id);
        this.paintPanel();
    },
    toggleGroup(ids, on) {
        ids.forEach(id => { if (on) this._picked.add(String(id)); else this._picked.delete(String(id)); });
        this.paintPanel();
    },

    async save() {
        if (this._saving || !this._selected.size || !this._picked.size) return;
        this._saving = true;
        this.paintPanel();
        const courseIds = [...this._picked];
        const ids = [...this._selected];
        let ok = 0;
        for (const id of ids) {
            if (await InventoryService.updateAllowedCourses(id, courseIds)) { ok++; this._selected.delete(id); }
        }
        this._saving = false;
        if (ok) Utils.showToast(ok === 1 ? 'Asignación guardada' : `${ok} elementos asignados`, 'success');
        if (ok < ids.length) Utils.showToast(`${ids.length - ok} no se pudieron guardar; siguen marcados.`, 'error');
        // What failed stays marked with the same formations, to try again.
        if (!this._selected.size) this._picked = new Set();
        this.paint();
    }
};

window.AssignmentManager = AssignmentManager;
